import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Import assets
import { Images } from "../../../utils/Images";

// Import product card components
import ProductBigCard from "../ProductCard/ProductBigCard";
import ProductSmallCard from "../ProductCard/ProductSmallCard";

// Import API service
import { getWishlist } from "../../../api/services/campaignService";

// Styled components
const Container = styled(Box)(({ theme, isMobile, isTablet, isSmallScreen }) => ({
  borderRadius: isSmallScreen ? "10px" : "12px",
  border: isMobile ? "none" : "1px solid #F1F1F1",
  padding: isMobile ? "0px" : isSmallScreen ? "20px" : isTablet ? "25px" : "30px",
  background: "#FFFFFF",
  width: "100%",
}));

const HeaderContainer = styled(Box)(({ theme, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
  marginBottom: "20px",
  flexWrap: isMobile ? "wrap" : "nowrap",
  rowGap: isMobile ? "8px" : undefined,
}));

const TitleSection = styled(Box)(({ theme, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  minWidth: isMobile ? 0 : undefined,
  flex: isMobile ? "1 1 auto" : undefined,
}));

const StarContainer = styled(Box)(({ theme, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& img": {
    width: isMobile ? "14px" : "24px",
    height: isMobile ? "14px" : "24px",
  },
}));

const ProductsTitle = styled(Typography)(({ theme, isMobile }) => ({
  // fontFamily: "Red Hat Display",
  fontWeight: 700,
  fontStyle: "Bold",
  fontSize: isMobile ? "14px" : "24px",
  lineHeight: isMobile ? "14px" : "24px",
  letterSpacing: "-2%",
  verticalAlign: "middle",
  color: "#1D1D1B",
}));

const WishlistButton = styled(Button)(({ theme, isMobile }) => ({
  width: isMobile ? "auto" : "125px",
  minWidth: isMobile ? "min-content" : "125px",
  height: isMobile ? "40px" : "46px",
  borderRadius: "8px",
  paddingTop: isMobile ? "10px" : "14px",
  paddingRight: isMobile ? "14px" : "24px",
  paddingBottom: isMobile ? "10px" : "14px",
  paddingLeft: isMobile ? "14px" : "24px",
//   gap: "10px",
  opacity: 1,
  background: "#1D1D1B",
  // fontFamily: "Red Hat Display",
  fontWeight: 600,
  fontStyle: "SemiBold",
  fontSize: isMobile ? "12px" : "14px",
  lineHeight: isMobile ? "12px" : "14px",
  letterSpacing: "0%",
//   textTransform: "capitalize",
  color: "#FFFFFF",
  textTransform: "none",
  transition: "all 0.2s ease",
  display: "inline-flex",
  flexShrink: 0,
  "&:hover": {
    background: "#2A2A28",
    boxShadow: "0px 4px 8px rgba(29, 29, 27, 0.3)",
  },
  "&:active": {
    transform: "translateY(1px)",
  },
  "& img": {
    width: "18px",
    height: "18px",
  },
}));

const HeaderBorder = styled(Box)(({ theme, isMobile }) => ({
  height: isMobile ? "0.25px" : "0.51px",
  background: "#B9B9B8",
  marginBottom: isMobile ? "14px" : "30px",
}));

const ProductsGrid = styled(Box)(({ theme, isMobile, isTablet, isSmallScreen }) => ({
  display: "flex",
  flexDirection: "column",
  gap: isSmallScreen ? "16px" : isTablet ? "18px" : "20px",
}));

// Unified grid so big and small cards can sit side‑by‑side.
// Uses the same sizing and gap behaviour that BigCardsGrid/SmallCardsGrid had.
const CardsGrid = styled(Box)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen }) => ({
  display: "grid",
  gridTemplateColumns: isMobile 
    ? "repeat(auto-fit, minmax(280px, 1fr))" 
    : isSmallScreen 
      ? "repeat(auto-fit, minmax(320px, 1fr))" 
      : isMediumScreen 
        ? "repeat(auto-fit, minmax(370px, 1fr))" 
        : "repeat(auto-fit, minmax(420px, 1fr))",
  gap: isMobile ? "12px" : isSmallScreen ? "16px" : isMediumScreen ? "18px" : "20px",
  marginBottom: isMobile ? "0px" : isSmallScreen ? "16px" : isMediumScreen ? "18px" : "20px",
  justifyItems: "center",
  alignItems: "center",
  "@media (min-width: 1440px)": {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "flex-start",
    justifyContent: "center",
    gridTemplateColumns: "none",
    gap: "20px",
  },
}));

// Special layout for 1 big card + 2 small cards stacked vertically in first row, then remaining small cards in second row
const FirstRowContainer = styled(Box)(({ theme, isMobile, isSmallScreen, isMediumScreen }) => ({
  display: isMobile ? "flex" : "flex",
  flexDirection: isMobile ? "column" : "row",
  flexWrap: "wrap",
  gap: isMobile ? "12px" : isSmallScreen ? "16px" : isMediumScreen ? "18px" : "20px",
  width: "100%",
  justifyContent: "center",
  alignItems: isMobile ? "center" : "flex-start",
  marginBottom: isMobile ? "0px" : isSmallScreen ? "16px" : isMediumScreen ? "18px" : "20px",
  "@media (min-width: 1440px)": {
    maxWidth: "none",
  },
}));

const BigCardWrapper = styled(Box)(({ theme, isMobile, isSmallScreen, isMediumScreen }) => ({
  // This wrapper will pass the extended prop to ProductBigCard
  // The actual height extension will be handled inside ProductBigCard component
}));

const SmallCardsStack = styled(Box)(({ theme, isMobile, isSmallScreen, isMediumScreen }) => ({
  display: "flex",
  flexDirection: "column",
  gap: isMobile ? "12px" : isSmallScreen ? "16px" : isMediumScreen ? "18px" : "20px",
  width: isMobile ? "100%" : isSmallScreen ? "330px" : isMediumScreen ? "380px" : "461px",
}));

// Second row container for remaining small cards - matches FirstRowContainer centering behavior
const SecondRowContainer = styled(Box)(({ theme, isMobile, isSmallScreen, isMediumScreen }) => ({
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
  gap: isMobile ? "12px" : isSmallScreen ? "16px" : isMediumScreen ? "18px" : "20px",
  width: "100%",
  justifyContent: "center",
    alignItems: "flex-start",
  marginBottom: isMobile ? "0px" : isSmallScreen ? "16px" : isMediumScreen ? "18px" : "20px",
}));

const CampaignProduct = ({ campaignData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery("(max-width: 900px)");
  const isSmallScreen = useMediaQuery("(max-width: 1200px)");
  const isMediumScreen = useMediaQuery("(max-width: 1420px)");
  const [wishlistLink, setWishlistLink] = useState(null);

  // Check if wishlist should be shown (WishList === 0 means show, WishList === 1 means hide)
  // Handle both string and number values
  const wishListValue = campaignData?.WishList;
  const shouldShowWishlist = wishListValue === 0 || wishListValue === "0" || Number(wishListValue) === 0;

  // Fetch wishlist on component mount only if wishlist should be shown
  useEffect(() => {
    if (!shouldShowWishlist) {
      return; // Don't fetch if wishlist should be hidden
    }

    const fetchWishlist = async () => {
      try {
        const response = await getWishlist();
        if (response && response.link) {
          setWishlistLink(response.link);
        }
      } catch (error) {
        console.error("Error fetching wishlist:", error);
      }
    };
    fetchWishlist();
  }, [shouldShowWishlist]);

  const handleWishlistClick = () => {
    if (wishlistLink) {
      window.open(wishlistLink, '_blank', 'noopener,noreferrer');
    }
  };

  // Get products from campaign data or use empty array
  const products = Array.isArray(campaignData?.products) ? campaignData.products : [];

  // Don't render if no products
  if (!products || products.length === 0) {
    return null;
  }

  // Separate big cards and small cards
  const bigCards = products.filter(product => product.cardSize === 1);
  const smallCards = products.filter(product => product.cardSize === 0);

  // Special layouts (desktop only):
  // 1) One big card + at least 2 small cards
  //    - Row 1: big[0] + 2 small stacked
  //    - Row 2: remaining small in normal grid
  // 2) Three big cards + 2 small cards
  //    - Row 1: big[0], big[1] in normal grid
  //    - Row 2: big[2] extended + 2 small stacked
  const isSpecialLayoutSingle = !isMobile && bigCards.length === 1 && smallCards.length >= 2;
  const isSpecialLayoutThree = !isMobile && bigCards.length === 3 && smallCards.length === 2;
  
  // For single‑big special layout
  const firstRowSmallCardsSingle = isSpecialLayoutSingle ? smallCards.slice(0, 2) : [];
  const remainingSmallCardsSingle = isSpecialLayoutSingle ? smallCards.slice(2) : [];
  
  // Order: all big cards first, then small cards (for regular layout)
  const orderedProducts = [...bigCards, ...smallCards];

  return (
    <Container isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen}>
      {/* Header Section */}
      <HeaderContainer isMobile={isMobile}>
        <TitleSection isMobile={isMobile}>
          <StarContainer isMobile={isMobile}>
            <img src={Images.streamlineSharpStar} alt="Star" />
          </StarContainer>
          <ProductsTitle isMobile={isMobile}>
            Products
          </ProductsTitle>
        </TitleSection>

        {/* Only show wishlist button if WishList === 0 */}
        {shouldShowWishlist && (
          <WishlistButton
            onClick={handleWishlistClick}
            isMobile={isMobile}
            disabled={!wishlistLink}
            startIcon={
              <img src={Images.pcart} alt="Cart" />
            }
          >
            Wishlist
          </WishlistButton>
        )}
      </HeaderContainer>

      <HeaderBorder isMobile={isMobile} />

      {/* Products Grid */}
      <ProductsGrid isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen}>
        {isSpecialLayoutSingle ? (
          <>
            {/* First Row: 1 big card + 2 small cards stacked vertically */}
            <FirstRowContainer isMobile={isMobile} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
              <BigCardWrapper isMobile={isMobile} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                <ProductBigCard 
                  key={bigCards[0].id}
                  productData={bigCards[0]}
                  cardIndex={0}
                  isExtended={true}
                  extendedHeight={isSmallScreen ? 316 : isMediumScreen ? 338 : 418}
                />
              </BigCardWrapper>
              <SmallCardsStack isMobile={isMobile} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                {firstRowSmallCardsSingle.map((product, index) => (
                  <ProductSmallCard 
                    key={product.id} 
                    productData={product}
                    cardIndex={index}
                  />
                ))}
              </SmallCardsStack>
            </FirstRowContainer>

            {/* Second Row: Remaining small cards centered like first row */}
            {remainingSmallCardsSingle.length > 0 && (
              <SecondRowContainer
                isMobile={isMobile}
                isSmallScreen={isSmallScreen}
                isMediumScreen={isMediumScreen}
              >
                {remainingSmallCardsSingle.map((product, index) => (
                  <ProductSmallCard
                    key={product.id}
                    productData={product}
                    cardIndex={index + 2} // Continue indexing from where first row left off
                  />
                ))}
              </SecondRowContainer>
            )}
          </>
        ) : isSpecialLayoutThree ? (
          <>
            {/* First Row: two big cards in normal grid */}
            <CardsGrid
              isMobile={isMobile}
              isTablet={isTablet}
              isSmallScreen={isSmallScreen}
              isMediumScreen={isMediumScreen}
            >
              {bigCards.slice(0, 2).map((product, index) => (
              <ProductBigCard 
                key={product.id} 
                productData={product}
                cardIndex={index}
              />
            ))}
            </CardsGrid>

            {/* Second Row: one big card + 2 small cards stacked, big card height extended */}
            <FirstRowContainer isMobile={isMobile} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
              <BigCardWrapper isMobile={isMobile} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                <ProductBigCard 
                  key={bigCards[2].id}
                  productData={bigCards[2]}
                  cardIndex={2}
                  isExtended={true}
                  extendedHeight={isSmallScreen ? 316 : isMediumScreen ? 338 : 418}
                />
              </BigCardWrapper>
              <SmallCardsStack isMobile={isMobile} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
            {smallCards.map((product, index) => (
                  <ProductSmallCard 
                    key={product.id} 
                    productData={product}
                    cardIndex={index}
                  />
                ))}
              </SmallCardsStack>
            </FirstRowContainer>
          </>
        ) : (
          // Regular grid layout
          <CardsGrid
            isMobile={isMobile}
            isTablet={isTablet}
            isSmallScreen={isSmallScreen}
            isMediumScreen={isMediumScreen}
          >
            {orderedProducts.map((product, index) =>
              product.cardSize === 1 ? (
                <ProductBigCard
                  key={product.id}
                  productData={product}
                  cardIndex={index}
                />
              ) : (
              <ProductSmallCard 
                key={product.id} 
                productData={product}
                cardIndex={index}
              />
              )
            )}
          </CardsGrid>
        )}
      </ProductsGrid>
    </Container>
  );
};

export default CampaignProduct;
